import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import Markdown from 'markdown-it';
import moment from 'moment';

import { AI_CONVERSATION_ROLES, DEFAULT_PRIORITY_TOKENS } from 'src/app/constants';
import {
  createPriorityValueToTokenMap,
  getIconForConversationPriority,
  isDev,
  isUrl as isURL,
} from 'src/app/utils/utils';
import { PriorityTokens, Profile } from 'src/models';
import {
  AGENT_CONFIDENCE_THRESHOLD,
  AIConversationMessage,
  AITesterAgentActivity,
  AITesterMessage,
} from 'src/models/ai-tester';
import { AgentMessage, AgentSelection, AITesterService, ASPECT_FORMATS } from 'src/services/ai-tester.service';
import { AuthService } from 'src/services/auth.service';
import { EmojisService } from 'src/services/emojis.service';
import { SearchablePopoverService } from 'src/services/searchable-popover.service';
import { TokenService } from 'src/services/token.service';
interface ImpersonatedUser {
  id: string;
  name: string;
  photoURL?: string;
}

@Component({
  selector: 'app-single-agent-tester',
  templateUrl: './single-agent-tester.component.html',
  styleUrls: ['./single-agent-tester.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SingleAgentTesterComponent implements OnInit {
  user: Profile;
  messageHistory: AIConversationMessage[] = [];
  displayedMessages: AITesterMessage[] = [];
  newMessage = '';
  isAgentThinking: boolean = false;
  isLastMessageError: boolean = false;
  isAgentNotResponding: boolean = false;
  isDrawerOpen: boolean = false;
  agents: AgentSelection[];
  selectedAgent: string;
  impersonatedUser?: ImpersonatedUser;
  customer?: { id: string; name: string };
  markdown = new Markdown({
    breaks: true,
    linkify: true,
  });
  priorityTokens: Record<string, PriorityTokens> = DEFAULT_PRIORITY_TOKENS;
  ASPECT_FORMATS = ASPECT_FORMATS;

  // Make the imported isURL available to the template
  public isURL = isURL;

  constructor(
    private authService: AuthService,
    private aiTesterService: AITesterService,
    private searchablePopoverService: SearchablePopoverService,
    private cd: ChangeDetectorRef,
    private tokenService: TokenService,
    private emojisService: EmojisService,
  ) {
    // bindings
    this.displayDateFrom = this.displayDateFrom.bind(this);
    this.intakeAgentMessageToAiTesterMessage = this.intakeAgentMessageToAiTesterMessage.bind(this);
  }

  async ngOnInit(): Promise<void> {
    await Promise.all([this.initConfig(), this.initializeCustomTokens()]);
  }

  async initConfig(): Promise<void> {
    // initialize user
    this.user = this.authService.profile;
    // initialize agent list
    this.agents = await this.aiTesterService.listAgents();
    this.selectedAgent = this.agents[0]?.id;

    // Set the current user as the impersonated user
    // This is because internal docs will only be visible to users
    // and if we don't set a user, they won't see good responses
    this.impersonatedUser = {
      id: this.user.id,
      name: this.user.name,
      photoURL: this.user.photo,
    };
  }

  async initializeCustomTokens() {
    return Promise.all([this.emojisService.loadEmojis(), this.initializePriorityTokens()]);
  }

  async initializePriorityTokens() {
    this.priorityTokens = (await this.tokenService.getResolvedTokens(['PRIORITY_LABEL_MAP'], this.customer?.id))[
      'PRIORITY_LABEL_MAP'
    ];
  }

  isDev() {
    return this.authService.isSuperAdmin || isDev();
  }

  clearTransientMessages() {
    this.isLastMessageError = false;
    this.isAgentNotResponding = false;
  }

  isViewAgentThoughtsEnabled() {
    return this.authService.tenant.viewAgentThoughtsEnabled || this.isDev();
  }

  toggleDrawer() {
    this.isDrawerOpen = !this.isDrawerOpen;
  }

  selectUser() {
    this.searchablePopoverService.allUsers({
      event,
      selectedUserId: this.impersonatedUser?.id,
      callback: async (id: string, name: string, user: any) => {
        this.impersonatedUser = {
          id,
          name,
          photoURL: user.photo,
        };
        this.clearMessages();
      },
    });
  }

  getObjectEntries(obj: any) {
    return Object.entries(obj).map(([key, value]) => ({ key, value }));
  }

  selectCustomer() {
    this.searchablePopoverService.customers({
      event,
      selectedCustomerId: this.customer?.id,
      projectId: this.authService.selectedProject?.id ?? undefined,
      callback: async (id: string, name: string) => {
        this.customer = {
          id,
          name,
        };
        this.clearMessages();
        // reinitialize priority tokens, which may be customer dependent
        await this.initializePriorityTokens();
      },
    });
  }

  async sendMessage() {
    this.clearTransientMessages();
    // return if message is empty
    if (this.newMessage.trim().length === 0) {
      return;
    }

    const message = {
      role: AI_CONVERSATION_ROLES.USER,
      content: this.newMessage.trim(),
      timestamp: this.displayDateFrom(new Date()),
    };
    // update state with user message
    this.updateMessageStates([message]);
    // clear input state, set loading state, and scroll down
    this.newMessage = '';
    this.isAgentThinking = true;
    this.scrollToBottom();
    // send message to endpoint
    const resp = await this.aiTesterService
      .sendMessage({
        agent: this.selectedAgent,
        history: this.messageHistory,
        impersonatedUserId: this.impersonatedUser?.id,
        customerId: this.customer?.id,
        projectId: this.authService.selectedProject?.id,
      })
      .catch((error) => {
        console.error(error);
        this.isLastMessageError = true;
      });
    // reset loading state
    this.isAgentThinking = false;
    // update state with replies from agent
    if (resp) {
      this.updateMessageStates(resp.map(this.intakeAgentMessageToAiTesterMessage));
    }
    this.cd.detectChanges();
  }

  scrollToBottom(): void {
    setTimeout(() => {
      const content = document.querySelector('ion-content');
      if (content) {
        content.scrollToBottom(300);
      }
    }, 100);
  }

  determineIfAiThoughtsShowLowConfidence(aiThoughts: AITesterAgentActivity[]): boolean {
    return aiThoughts.some((thought) => {
      return (
        thought.aspects &&
        ((thought.aspects.topic_selection_confidence != undefined &&
          thought.aspects.topic_selection_confidence < AGENT_CONFIDENCE_THRESHOLD) ||
          (thought.aspects.action_to_take_confidence != undefined &&
            thought.aspects.action_to_take_confidence < AGENT_CONFIDENCE_THRESHOLD))
      );
    });
  }

  intakeAgentMessageToAiTesterMessage(intakeAgentMessage: AgentMessage): AITesterMessage {
    const formattedThoughts = intakeAgentMessage.formattedThoughts?.map((thought) => ({
      ...thought,
      showThoughtAspects: false,
    }));

    return {
      ...intakeAgentMessage,
      timestamp: this.displayDateFrom(new Date()),
      formattedThoughts,
      aiThoughtsShowLowConfidence: formattedThoughts
        ? this.determineIfAiThoughtsShowLowConfidence(formattedThoughts)
        : undefined,
    };
  }

  displayDateFrom(date: Date): string {
    return moment(date).fromNow();
  }

  updateMessageStates(newMessages: AITesterMessage[]): void {
    const filteredMessages = newMessages.filter((message) => message.content != null && message.content != '');

    if (filteredMessages.length === 0) {
      this.isAgentNotResponding = true;
      return;
    }

    this.displayedMessages.push(...filteredMessages);
    this.messageHistory.push(
      ...filteredMessages.map(({ role, content }) => ({
        role,
        content,
      })),
    );
  }

  getEmojiForPriority(priority: string) {
    return this.priorityTokens[priority]?.EMOJI;
  }

  getPriorityLabel(priority: string) {
    return this.priorityTokens[priority]?.LABEL;
  }

  isBot(role: string): boolean {
    return role === AI_CONVERSATION_ROLES.ASSISTANT;
  }

  handleKeyDown(event: KeyboardEvent) {
    if (event.key === 'Enter' && !event.shiftKey) {
      // handle pressing enter to send
      // prevent the default Enter behavior of adding a newline
      event.preventDefault();
      // trigger the send message functionality
      this.sendMessage();
    } else if (event.key === 'Enter' && event.shiftKey) {
      // handle shift + enter for newline
      this.newMessage += '\n';
    }
  }

  convertToMarkdown(text: string): string {
    return this.markdown.render(text);
  }

  clearMessages(): void {
    this.messageHistory = [];
    this.displayedMessages = [];
  }
}
